import { inject, Injectable } from '@angular/core';
import { GainsightAnalyticsService } from './gainsight-analytics.service';
import { EnvironmentService } from '../environment.service';
import { HostApplicationService } from '../host-application.service';
import { HostAppContext } from '../host-application.interfaces';

type AnalyticsProperties = Record<string, string | number | boolean>;

/**
 * Aura-specific analytics facade over {@link GainsightAnalyticsService}.
 *
 * This is the single source of analytics property construction for the chat
 * experience: callers pass only the domain values they own (agent, session,
 * run, etc.) and this service enriches every event with the shared base
 * properties (agentId, host, platform) before forwarding to Gainsight.
 */
@Injectable({ providedIn: 'root' })
export class AuraAnalyticsService extends GainsightAnalyticsService {
  #environmentService = inject(EnvironmentService);
  #hostApplicationService = inject(HostApplicationService);

  public trackAuraOpened(agentId: string | null): void {
    // Fires during app init, before the async Gainsight init/identify completes.
    // Wait for readiness so the event is not queued ahead of `identify` (which
    // causes Gainsight PX to drop it).
    this.whenReady().finally(() => {
      this.trackEvent('Aura Opened', this.buildBaseProperties(agentId));
    });
  }

  public trackPromptSubmitted(params: {
    agentId: string | null;
    source: string;
    sessionId: string | null;
    conversationLength: number;
    isTemporary: boolean;
  }): void {
    this.trackEvent('Prompt Submitted', this.buildBaseProperties(params.agentId, {
      source: params.source,
      sessionId: params.sessionId ?? '',
      conversationLength: params.conversationLength,
      isTemporary: params.isTemporary,
    }));
  }

  public trackSuggestionSelected(agentId: string | null, suggestion: string): void {
    this.trackEvent('Suggestion Selected', this.buildBaseProperties(agentId, {
      suggestion,
    }));
  }

  public trackResponseCompleted(params: {
    agentId: string | null;
    sessionId: string | null;
    runId: string | null;
  }): void {
    this.trackEvent('Response Completed', this.buildBaseProperties(params.agentId, {
      sessionId: params.sessionId ?? '',
      runId: params.runId ?? '',
    }));
  }

  public trackResponseFailed(params: {
    agentId: string | null;
    sessionId: string | null;
    runId: string | null;
  }): void {
    this.trackEvent('Response Failed', this.buildBaseProperties(params.agentId, {
      sessionId: params.sessionId ?? '',
      runId: params.runId ?? '',
    }));
  }

  public trackFeedbackSubmitted(params: {
    agentId: string | null;
    sessionId: string | null;
    runId: string | null;
    isPositive: boolean;
    feedbackText: string;
  }): void {
    this.trackEvent('Feedback Submitted', this.buildBaseProperties(params.agentId, {
      sessionId: params.sessionId ?? '',
      runId: params.runId ?? '',
      isPositive: params.isPositive.toString(),
      feedbackText: params.feedbackText,
    }));
  }

  /**
   * Resolves account (organisation) details from any platform section that
   * exposes them, so `identify` sends org details across all platforms rather
   * than only FC and On-Road. `user_id` is always sent by the base identify.
   */
  protected override resolveAccountDetails(context: HostAppContext): { id: string; name: string } {
    for (const section of Object.values(context)) {
      const org = section as { org_id?: string; org_name?: string } | undefined;
      if (org && (org.org_id || org.org_name)) {
        return { id: org.org_id || 'N/A', name: org.org_name || 'N/A' };
      }
    }
    return { id: 'N/A', name: 'N/A' };
  }

  private buildBaseProperties(agentId: string | null, extra: AnalyticsProperties = {}): AnalyticsProperties {
    return {
      agentId: agentId ?? '',
      host: this.#environmentService.host,
      platform: this.resolvePlatform(),
      ...extra,
    };
  }

  private resolvePlatform(): string {
    const context = this.#hostApplicationService.getCurrentContext();
    if (!context) {
      return '';
    }
    // Prefer the enveloped contract; fall back to the flat contract that some
    // hosts (e.g. On-Road) still send.
    const flat = context as unknown as Record<string, unknown>;
    return (
      context['embedded.aura.unityaiot.com']?.context_platform ??
      (typeof flat['platform'] === 'string' ? flat['platform'] as string : '')
    );
  }
}
